import React from "react";
import { Button, Column, Row, Sheet, Text, Icon } from "@amzn/meridian";
import errorTokens from "@amzn/meridian-tokens/base/icon/error";

/** A single action button rendered in the ErrorSheet. */
export interface ErrorSheetAction {
    label: string;
    onClick: () => void;
    /** Meridian Button type. Defaults to "primary". */
    type?: "primary" | "secondary" | "tertiary" | "link";
}

export interface ErrorSheetProps {
    /** Controls visibility. The parent owns this state. */
    open: boolean;
    /** Bold header line. */
    title?: string;
    /** Secondary explanatory line. */
    message?: string;
    /** Called when the sheet is dismissed (tap outside or the Dismiss button). */
    onClose: () => void;
    /** Optional action buttons (e.g. "Try Again"), rendered above Dismiss. */
    actions?: ErrorSheetAction[];
    /** Whether to render the built-in Dismiss button. Defaults to true. */
    showDismiss?: boolean;
    /** Label for the built-in Dismiss button. Defaults to "Dismiss". */
    dismissLabel?: string;
}

/**
 * Generic bottom sheet for surfacing an error to the user. Shares the visual
 * language of StepTransitionSheet (overlay bottom sheet, centred icon + text)
 * but is fully controlled by the parent and supports arbitrary action buttons
 * so any feature can reuse it for its own error messaging.
 *
 * Usage:
 *   const [error, setError] = useState(false);
 *   <ErrorSheet
 *     open={error}
 *     title="Couldn't start inspection"
 *     message="Something went wrong. Please try again."
 *     onClose={() => setError(false)}
 *     actions={[{ label: "Try Again", onClick: handleRetry }]}
 *   />
 */
const ErrorSheet: React.FC<ErrorSheetProps> = ({
    open,
    title = "Something went wrong",
    message = "Please try again.",
    onClose,
    actions = [],
    showDismiss = true,
    dismissLabel = "Dismiss",
}) => {
    return (
        <Sheet type="overlay" side="bottom" open={open} onClose={onClose}>
            <Column spacing="400">
                <Row alignmentHorizontal="center">
                    <Column spacing="200" alignmentHorizontal="center">
                        <Icon tokens={errorTokens} color="error" />
                        <Text type="h400">{title}</Text>
                        <Text type="b100" color="secondary">
                            {message}
                        </Text>
                    </Column>
                </Row>
                {(actions.length > 0 || showDismiss) && (
                    <Column spacing="300">
                        {actions.map((action, index) => (
                            <Button
                                key={`${action.label}-${index}`}
                                size="large"
                                type={action.type ?? "primary"}
                                onClick={action.onClick}
                            >
                                {action.label}
                            </Button>
                        ))}
                        {showDismiss && (
                            <Button size="large" type="tertiary" onClick={onClose}>
                                {dismissLabel}
                            </Button>
                        )}
                    </Column>
                )}
            </Column>
        </Sheet>
    );
};

export default ErrorSheet;
